
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { HistoryLogParams, BaseHistoryItem, HistoryEntityType, HistoryActionType, HistoryChange } from '@/types/history';
import { useSettings } from '@/providers/SettingsProvider';
import { toast } from '@/components/ui/use-toast';

// Define specific record types for each history table
interface HistoryRecord {
  id?: string;
  action: string;
  description: string;
  performed_by: string;
  performed_at?: string;
}

interface CandidateHistoryRecord extends HistoryRecord {
  candidate_id: string;
}

interface JobHistoryRecord extends HistoryRecord {
  job_id: string;
}

interface CompanyHistoryRecord extends HistoryRecord {
  company_id: string;
}

interface TableConfig {
  tableName: string;
  idField: string;
}

export const useHistoryOperations = (entityType: HistoryEntityType) => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<BaseHistoryItem[]>([]);
  const { dateFormat, timeFormat } = useSettings();

  // Get the table configuration based on entity type
  const getTableConfig = (type: HistoryEntityType): TableConfig => {
    switch (type) {
      case 'candidate':
        return {
          tableName: 'candidate_history',
          idField: 'candidate_id'
        };
      case 'job':
        return {
          tableName: 'job_history',
          idField: 'job_id'
        };
      case 'company':
        return {
          tableName: 'company_history',
          idField: 'company_id'
        };
      default:
        throw new Error(`Invalid entity type: ${type}`);
    }
  };

  const getCurrentUser = async (): Promise<string> => {
    try {
      let userName = "System User";
      const { data: userSession } = await supabase.auth.getSession();
      
      if (userSession.session?.user) {
        const { data: userProfile } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', userSession.session.user.id)
          .single();
        
        if (userProfile) {
          userName = `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim();
          if (!userName) userName = "System User";
        }
      }
      
      return userName;
    } catch (error) {
      console.error("Error getting current user:", error);
      return "System User";
    }
  };

  const fetchHistory = useCallback(async (entityId: string) => {
    if (!entityId) return;

    try {
      setLoading(true);
      const { tableName, idField } = getTableConfig(entityType);

      console.log(`Fetching history for ${entityType} with ID ${entityId} from table ${tableName}`);

      // Use type assertion to avoid type instantiation error
      const { data, error } = await supabase
        .from(tableName as any)
        .select('*')
        .eq(idField, entityId)
        .order('performed_at', { ascending: false });

      if (error) {
        console.error(`Error fetching ${entityType} history:`, error);
        throw error;
      }

      console.log(`Fetched ${data?.length || 0} history items:`, data);

      // Map the data to BaseHistoryItem structure with type assertion
      const historyItems: BaseHistoryItem[] = (data || []).map((item: any) => ({
        id: item.id,
        action: item.action,
        description: item.description,
        performed_by: item.performed_by,
        performed_at: item.performed_at
      }));

      setHistory(historyItems);
    } catch (error) {
      console.error(`Error fetching ${entityType} history:`, error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [entityType]);

  const logHistory = async ({
    entityId,
    entityType,
    action,
    description,
    change,
    performedBy
  }: HistoryLogParams): Promise<boolean> => {
    try {
      const { tableName, idField } = getTableConfig(entityType);
      const userName = performedBy || await getCurrentUser();

      let finalDescription = description;
      
      // Format change information if provided with old and new values
      if (change) {
        // Skip logging if both values are empty (null, undefined, or empty string)
        const oldValueEmpty = !change.oldValue || change.oldValue.trim() === '';
        const newValueEmpty = !change.newValue || change.newValue.trim() === '';
        
        if (oldValueEmpty && newValueEmpty) {
          console.log(`Skipping history log for ${change.field}: both values are empty`);
          return false;
        }
        
        finalDescription = `${change.field}: Changed from "${change.oldValue || ''}" to "${change.newValue || ''}"`;
      }

      // Create the appropriate history record based on entity type
      let historyEntry: CandidateHistoryRecord | JobHistoryRecord | CompanyHistoryRecord;
      
      if (entityType === 'candidate') {
        historyEntry = {
          candidate_id: entityId,
          action,
          description: finalDescription,
          performed_by: userName
        };
      } else if (entityType === 'job') {
        historyEntry = {
          job_id: entityId,
          action,
          description: finalDescription,
          performed_by: userName
        };
      } else if (entityType === 'company') {
        historyEntry = {
          company_id: entityId,
          action,
          description: finalDescription,
          performed_by: userName
        };
      } else {
        throw new Error(`Invalid entity type: ${entityType}`);
      }

      console.log(`Logging history for ${entityType}:`, historyEntry);

      // Insert the history record
      const { error } = await supabase
        .from(tableName as any)
        .insert(historyEntry);

      if (error) {
        console.error(`Error inserting ${entityType} history:`, error);
        toast({
          title: "Error",
          description: `Failed to log history: ${error.message}`,
          variant: "destructive"
        });
        throw error;
      }

      // Refresh history after logging a new entry
      await fetchHistory(entityId);
      return true;
    } catch (error) {
      console.error(`Error logging ${entityType} history:`, error);
      return false;
    }
  };

  return {
    history,
    loading,
    fetchHistory,
    logHistory
  };
};
